import style from "css/hotdesking.module.css";
import { Button, Dialog, FloatingPanel, Loading } from "antd-mobile";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ReactComponent as CheckInIcon } from "asset/hotdesking/checkin.svg";
import { ReactComponent as CheckOutIcon } from "asset/hotdesking/m_checkout.svg";
import { ReactComponent as SeatIcon } from "asset/hotdesking/m_seat.svg";
import { ReactComponent as ShareIcon } from "asset/hotdesking/m_share.svg";
import { ReactComponent as ReloadIcon } from "asset/hotdesking/reload.svg";
import { ReactComponent as SpotIcon } from "asset/hotdesking/spot.svg";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import BookShare from "./BookShare";
import CheckOut from "./CheckOut";
import axios from "axios";
import HotdeskingConfig from "../Config";
import CheckIn from "./CheckIn";
import BookCancel from "./BookCancel";
import BookExtend from "./BookExtend";
import BookResource from "./BookResource";
import { useTranslation } from "react-i18next";
dayjs.locale("ko");

const contentHeight = window.innerHeight - 35;
const anchors = [30, 150, contentHeight];
const viewType = ["reduce", "default", "expand"];
const reduceType = { type: viewType[0], height: anchors[0] };
const defaultType = { type: viewType[1], height: anchors[1] };
const expandType = { type: viewType[2], height: anchors[2] };

const type = {
  available: "AVAILABLE",
  in_use: "IN_USE",
  not_use: "NOT_USE",
  error: "ERROR",
};

const BookFloatingPanel = ({
  book,
  serverDate,
  date,
  time,
  setTime,
  resource,
  setResource,
  onRefreshBook,
  onShowResource,
  onError,
}) => {
  const { i18n } = useTranslation();
  dayjs.locale(i18n.language);
  const user = useSelector((state) => state.user);
  //ref
  const contentRef = useRef(null);
  //state
  const [isShare, setIsShare] = useState(false);

  const [init, setInit] = useState(true);
  const [view, setView] = useState(defaultType.type);
  const [isDragging, setIsDragging] = useState(false);
  const [isCalculate, setIsCalculate] = useState(false);
  const [ratio, setRatio] = useState(0);

  const onHeightChange = (height, animating) => {
    if (animating) {
      if (!isDragging) setIsDragging(true);
      return;
    }
    switch (height) {
      case reduceType.height:
        setView(reduceType.type);
        break;
      case defaultType.height:
        setView(defaultType.type);
        break;
      case expandType.height:
        setView(expandType.type);
        break;
      default:
        setRatio(height / expandType.height);
        setIsCalculate(true);
        return;
    }
    setIsDragging(false);
  };
  const calculateHeight = useCallback(() => {
    if (ratio < 0.25) {
      contentRef.current.setHeight(reduceType.height);
      setView(reduceType.type);
    } else if (ratio >= 0.3) {
      contentRef.current.setHeight(expandType.height);
      setView(expandType.type);
    } else {
      contentRef.current.setHeight(defaultType.height);
      setView(defaultType.type);
    }
    setRatio(0);
    setIsDragging(false);
  }, [ratio]);
  useLayoutEffect(() => {
    if (contentRef.current && init) {
      contentRef.current.setHeight(defaultType.height);
      setInit(false);
    }
  }, [init]);
  useEffect(() => {
    if (isCalculate) {
      calculateHeight();
      setIsCalculate(false);
    }
  }, [calculateHeight, isCalculate]);
  //공유 팝업
  const onOpenShare = () => {
    setIsShare(true);
  };
  const onCloseShare = () => {
    setIsShare(false);
  };
  const onClickResource = (
    departmentId,
    departmentTitle,
    locationId,
    locationTitle,
    locationSource,
    resourceId,
    resourceTitle,
    resourceType,
    coordinateX,
    coordinateY
  ) => {
    onShowResource(
      departmentId,
      departmentTitle,
      locationId,
      locationTitle,
      locationSource,
      resourceId,
      resourceTitle,
      resourceType,
      coordinateX,
      coordinateY
    );
    contentRef.current.setHeight(reduceType.height);
    setView(reduceType.type);
  };

  const contentRender = () => {
    if (!init && book !== null) {
      switch (book.status) {
        case type.available:
          return (
            <Available
              view={view}
              user={user}
              book={book}
              onRefreshBook={onRefreshBook}
              onShare={onOpenShare}
              onShowResource={onClickResource}
              onError={onError}
            />
          );
        case type.in_use:
          return (
            <InUse
              view={view}
              user={user}
              book={book}
              onRefreshBook={onRefreshBook}
              onShare={onOpenShare}
              onShowResource={onClickResource}
              onError={onError}
            />
          );
        case type.not_use:
          return (
            <NotUse
              view={view}
              user={user}
              serverDate={serverDate}
              date={date}
              time={time}
              setTime={setTime}
              resource={resource}
              setResource={setResource}
              onRefreshBook={onRefreshBook}
              onShowResource={onClickResource}
              onError={onError}
            />
          );
        default:
          return <Loading className={style.ticket_loading} />;
      }
    } else {
      return (
        <NotUse
          view={view}
          user={user}
          serverDate={serverDate}
          date={date}
          time={time}
          setTime={setTime}
          resource={resource}
          setResource={setResource}
          onRefreshBook={onRefreshBook}
          onShowResource={onClickResource}
          onError={onError}
        />
      );
    }
  };
  return (
    <>
      <BookShare
        book={book}
        isOpen={isShare}
        onClose={onCloseShare}
        onError={onError}
      />
      <FloatingPanel
        className={style.ticket_panel}
        anchors={anchors}
        onHeightChange={onHeightChange}
        placement="bottom"
        handleDraggingOfContent={false}
        ref={contentRef}
      >
        <section className={style.ticket_wrap}>
          {!isDragging && contentRender()}
        </section>
      </FloatingPanel>
    </>
  );
};
export default BookFloatingPanel;
const formatDate = (date, language) => {
  if (language === "ko-KR") {
    return dayjs(new Date(date)).format("YYYY년 MM월 DD일 (ddd요일)");
  } else {
    return dayjs(new Date(date)).format("YYYY. MM. DD. (ddd)");
  }
};
const formatTime = (startDttm, endDttm) => {
  const formatStartDttm = dayjs(new Date(startDttm));
  const formatEndDttm = dayjs(new Date(endDttm));
  return (
    <>
      {formatStartDttm.format("HH:mm")} ~ {formatEndDttm.format("HH:mm")}
    </>
  );
};
const formatDuration = (duration, language) => {
  let hours = Math.floor(duration / 60);
  let mins = duration % 60;
  if (language === "ko-KR") {
    return `${String(hours).padStart(2, "0")}시간 ${String(mins).padStart(
      2,
      "0"
    )}분`;
  } else {
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  }
};
function Available({
  view,
  user,
  book,
  onRefreshBook,
  onShare,
  onShowResource,
  onError,
}) {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const onCheckIn = () => {
    setIsLoading(true);
    const data = {
      book_id: book.id,
      start_dttm: book.startDttm,
      status: "IN_USE",
    };
    axios
      .put(`${HotdeskingConfig.baseURI}/book/status`, data)
      .then(() => {
        onRefreshBook();
      })
      .catch((err) => {
        if (err === undefined || err.response === undefined) {
          onError(t("HOTDESKING.MODAL.CHECK_IN.MESSAGE.ERROR"));
        } else {
          onError(err.response.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const onOpenCheckIn = () => {
    Dialog.show({
      className: `${style.dialog} ${style.confirm}`,
      bodyClassName: `${style.dialog_body}`,
      bodyStyle: { padding: 0 },
      content: <CheckIn onSubmit={onCheckIn} />,
    });
  };
  const onCancel = () => {
    const config = {
      params: {
        book_id: book.id,
      },
    };
    axios
      .delete(`${HotdeskingConfig.baseURI}/book/cancel`, config)
      .then(() => {
        onRefreshBook();
      })
      .catch((err) => {
        if (err === undefined || err.response === undefined) {
          onError(t("HOTDESKING.MODAL.CANCEL.MESSAGE.ERROR"));
        } else {
          // onError(err.response.data);
          onError(t("HOTDESKING.MODAL.CANCEL.MESSAGE.CANNOT"));
        }
      });
  };
  const onOpenCancel = () => {
    Dialog.show({
      className: `${style.dialog} ${style.confirm}`,
      bodyClassName: `${style.dialog_body}`,
      bodyStyle: { padding: 0 },
      content: <BookCancel onSubmit={onCancel} />,
    });
  };
  const contentRender = () => {
    switch (view) {
      case defaultType.type:
        return (
          <div className={style.ticket_box}>
            <div className={style.info_box}>
              <article className={style.info_article}>
                <div className={style.info_item}>
                  <p className={style.title}>Location</p>
                  <p className={style.label}>{book?.resource.location.title}</p>
                </div>
                <span className={style.driver} />
                <div className={style.info_item}>
                  <p className={style.title}>No.</p>
                  <p className={style.label}>{book?.resource.title}</p>
                </div>
              </article>
            </div>
            <div className={style.button_box} onClick={onOpenCheckIn}>
              <CheckInIcon />
              <p>{t("HOTDESKING.MENU.AVAILABLE.BUTTON.CHECK_IN")}</p>
            </div>
          </div>
        );
      case expandType.type:
        return (
          <section
            className={style.ticket_expand}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <section className={style.expand_content}>
              <section className={style.content_header}>
                <p>Today</p>
              </section>
              <section className={style.content_body}>
                <div className={style.use_ticket_box}>
                  <div className={style.use_top_box}>
                    <div className={style.banner}>
                      {formatDate(book.startDttm, i18n.language)}
                    </div>

                    <div className={style.comment}>
                      <p>
                        {t("HOTDESKING.MENU.AVAILABLE.TITLE", {
                          NAME: user.name,
                        })}
                      </p>
                    </div>
                  </div>

                  <div className={style.use_bottom_box}>
                    <p className={style.department}>
                      {book.resource.location.departmentTitle}
                    </p>

                    <div className={style.location}>
                      <div className={style.item}>
                        <p>{book.resource.location.title}</p>
                        <p>Location</p>
                      </div>

                      <span className={style.divider} />

                      <div className={style.item}>
                        <p>{book.resource.title}</p>
                        <p>No.</p>
                      </div>
                    </div>

                    <article className={style.duration}>
                      <p>{formatTime(book.startDttm, book.endDttm)}</p>
                      <p>({formatDuration(book.duration, i18n.language)})</p>
                    </article>
                  </div>
                </div>
              </section>

              <section className={style.content_footer}>
                <Button className={style.footer_button} onClick={onOpenCheckIn}>
                  <div className={style.button_content}>
                    <CheckInIcon className={style.button_icon} />
                    <p>{t("HOTDESKING.MENU.AVAILABLE.BUTTON.CHECK_IN")}</p>
                  </div>
                </Button>

                <section className={style.footer_options}>
                  <div
                    className={style.item}
                    onClick={() =>
                      onShowResource(
                        book?.resource.location.departmentId,
                        book?.resource.location.departmentTitle,
                        book?.resource.location.id,
                        book?.resource.location.title,
                        book?.resource.location.source,
                        book?.resource.id,
                        book?.resource.title,
                        book?.resource.type,
                        book?.resource.coordinateX,
                        book?.resource.coordinateY
                      )
                    }
                  >
                    <SeatIcon />
                    <p>
                      {t("HOTDESKING.MENU.AVAILABLE.BUTTON.MY_RESERVATION")}
                    </p>
                  </div>

                  <span className={style.divider} />

                  <div className={style.item}>
                    <div className={style.item} onClick={onShare}>
                      <ShareIcon />
                      <p>{t("HOTDESKING.MENU.AVAILABLE.BUTTON.SHARE")}</p>
                    </div>
                  </div>
                </section>

                <section className={style.expand_footer}>
                  <Button className={style.button} onClick={onOpenCancel}>
                    {t("HOTDESKING.MENU.AVAILABLE.BUTTON.CANCEL")}
                  </Button>
                </section>
              </section>
            </section>
          </section>
        );
      default:
        return <></>;
    }
  };
  return (
    <section
      className={`${style.ticket_container} ${style.available}`}
      style={{
        maxHeight: view === defaultType.type && defaultType.height - 64,
        height: "100%",
        width: "100%",
      }}
    >
      {isLoading ? (
        <Loading className={style.ticket_loading} />
      ) : (
        contentRender()
      )}
    </section>
  );
}
function InUse({
  view,
  user,
  book,
  onRefreshBook,
  onShare,
  onShowResource,
  onError,
}) {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenExtend, setIsOpenExtend] = useState(false);
  const onCheckOut = () => {
    setIsLoading(true);
    const data = {
      book_id: book.id,
      status: "END_USE",
    };
    axios
      .put(`${HotdeskingConfig.baseURI}/book/status`, data)
      .then(() => {
        onRefreshBook();
      })
      .catch((err) => {
        if (err === undefined || err.response === undefined) {
          onError(t("HOTDESKING.MODAL.CHECK_OUT.MESSAGE.ERROR"));
        } else {
          onError(err.response.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const onOpenCheckOut = () => {
    Dialog.show({
      className: `${style.dialog} ${style.confirm}`,
      bodyClassName: `${style.dialog_body}`,
      bodyStyle: { padding: 0 },
      content: <CheckOut onSubmit={onCheckOut} />,
    });
  };
  const onOpenExtend = () => {
    setIsOpenExtend(true);
  };
  const onCloseExtend = () => {
    setIsOpenExtend(false);
  };
  const contentRender = () => {
    switch (view) {
      case defaultType.type:
        return (
          <div className={style.ticket_box}>
            <div className={style.info_box}>
              <article className={style.info_article}>
                <div className={style.info_item}>
                  <p className={style.title}>Location</p>
                  <p className={style.label}>{book.resource.location.title}</p>
                </div>
                <span className={style.driver} />
                <div className={style.info_item}>
                  <p className={style.title}>No.</p>
                  <p className={style.label}>{book.resource.title}</p>
                </div>
              </article>
            </div>
            <div className={style.button_box} onClick={onOpenCheckOut}>
              <CheckOutIcon />
              <p>{t("HOTDESKING.MENU.IN_USE.BUTTON.CHECK_OUT")}</p>
            </div>
          </div>
        );
      case expandType.type:
        return (
          <>
            <BookExtend
              isOpen={isOpenExtend}
              book={book}
              onClose={onCloseExtend}
              onRefreshBook={onRefreshBook}
              onError={onError}
            />
            <section
              className={style.ticket_expand}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              <section className={style.expand_content}>
                <section className={style.content_header}>
                  <p>Today</p>
                </section>
                <section className={style.content_body}>
                  <div className={style.use_ticket_box}>
                    <div className={style.use_top_box}>
                      <div className={style.banner}>
                        {formatDate(book.startDttm, i18n.language)}
                      </div>

                      <div className={style.comment}>
                        <p>
                          {t("HOTDESKING.MENU.IN_USE.TITLE", {
                            NAME: user.name,
                          })}
                        </p>
                      </div>
                    </div>

                    <div className={style.use_bottom_box}>
                      <p className={style.department}>
                        {book.resource.location.departmentTitle}
                      </p>

                      <div className={style.location}>
                        <div className={style.item}>
                          <p>{book.resource.location.title}</p>
                          <p>Location</p>
                        </div>

                        <span className={style.divider} />

                        <div className={style.item}>
                          <p>{book.resource.title}</p>
                          <p>No.</p>
                        </div>
                      </div>

                      <article className={style.duration}>
                        <p>{formatTime(book.startDttm, book.endDttm)}</p>
                        <p>({formatDuration(book.duration, i18n.language)})</p>
                      </article>
                    </div>
                  </div>
                </section>

                <section className={style.content_footer}>
                  <Button
                    className={style.footer_button}
                    onClick={onOpenCheckOut}
                  >
                    <div className={style.button_content}>
                      <p>{t("HOTDESKING.MENU.IN_USE.BUTTON.CHECK_OUT")}</p>
                      <CheckOutIcon className={style.button_icon} />
                    </div>
                  </Button>

                  <section className={style.footer_options}>
                    <div
                      className={style.item}
                      onClick={() =>
                        onShowResource(
                          book?.resource.location.departmentId,
                          book?.resource.location.departmentTitle,
                          book?.resource.location.id,
                          book?.resource.location.title,
                          book?.resource.location.source,
                          book?.resource.id,
                          book?.resource.title,
                          book?.resource.type,
                          book?.resource.coordinateX,
                          book?.resource.coordinateY
                        )
                      }
                    >
                      <SeatIcon />
                      <p>{t("HOTDESKING.MENU.IN_USE.BUTTON.MY_RESERVATION")}</p>
                    </div>

                    <span className={style.divider} />

                    <div className={style.item}>
                      <div className={style.item} onClick={onShare}>
                        <ShareIcon />
                        <p>{t("HOTDESKING.MENU.IN_USE.BUTTON.SHARE")}</p>
                      </div>
                    </div>
                  </section>

                  <section className={style.expand_footer}>
                    <Button className={style.button} onClick={onOpenExtend}>
                      {t("HOTDESKING.MENU.IN_USE.BUTTON.EXTEND")}
                    </Button>
                  </section>
                </section>
              </section>
            </section>
          </>
        );
      default:
        return <></>;
    }
  };
  return (
    <section
      className={`${style.ticket_container} ${style.inuse}`}
      style={{
        maxHeight: view === defaultType.type && defaultType.height - 64,
        height: "100%",
        width: "100%",
      }}
    >
      {isLoading ? (
        <Loading className={style.ticket_loading} />
      ) : (
        contentRender()
      )}
    </section>
  );
}
function NotUse({
  view,
  user,
  serverDate,
  date,
  time,
  setTime,
  resource,
  setResource,
  onRefreshBook,
  onShowResource,
  onError,
}) {
  const { t } = useTranslation();
  //system redux
  const systemPayload = useSelector((state) => state.system);

  const [isLoading, setIsLoading] = useState(false);
  const [isSearch, setIsSearch] = useState(true);

  const getResource = useCallback(() => {
    setIsLoading(true);
    const config = {
      params: {
        start_dttm: date + " " + time.start,
        end_dttm: date + " " + time.end,
      },
    };
    axios
      .get(`${HotdeskingConfig.baseURI}/book/resource`, config)
      .then((res) => {
        setResource(res.data);
      })
      .catch((err) => {
        if (err === undefined || err.response === undefined) {
          onError(t("HOTDESKING.MENU.NOT_USE.MESSAGE.CANNOT_FIND_SEAT"));
        } else {
          onError(err.response.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [date, onError, setResource, t, time.end, time.start]);

  useEffect(() => {
    if (isSearch && resource === null) {
      getResource();
      setIsSearch(false);
    }
  }, [getResource, isSearch, resource]);

  const formatStartTime = () => {
    if (date === serverDate) {
      const nowTime = dayjs(new Date()).format("HH:mm:ss");

      if (time.start > nowTime) {
        return dayjs(new Date(date + " " + time.start)).format(
          "YYYY-MM-DD HH:mm:ss"
        );
      }

      setTime({
        ...time,
        start: nowTime,
      });
      return dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
    }
    return dayjs(new Date(date + " " + time.start)).format(
      "YYYY-MM-DD HH:mm:ss"
    );
  };

  const onBook = (resource) => {
    setIsLoading(true);
    const data = {
      resource_type: resource.type,
      resource_id: resource.id,
      start_dttm: formatStartTime(),
      end_dttm: dayjs(new Date(date + " " + time.end)).format(
        "YYYY-MM-DD HH:mm:ss"
      ),
    };

    if (resource.endpoint !== undefined) {
      const endpoint = {
        endpoint_id: resource.endpoint.id,
        endpoint_name: resource.endpoint.name,
        endpoint_type: resource.endpoint.type,
      };
      data.endpoint = endpoint;
    }

    axios
      .post(`${HotdeskingConfig.baseURI}/book`, data)
      .then(() => {
        onRefreshBook();
      })
      .catch((err) => {
        if (err === undefined || err.response === undefined) {
          onError(t("HOTDESKING.MODAL.BOOKING.MESSAGE.ERROR"));
        } else {
          onError(err.response.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const onOpenBook = () => {
    if (!systemPayload.isOnline) {
      Dialog.show({
        className: `${style.dialog} ${style.confirm}`,
        bodyClassName: `${style.dialog_body}`,
        bodyStyle: { padding: 0 },
        content: <BookResource onSubmit={() => onBook(resource)} />,
      });
      return;
    }
    if (resource.type === "CD1401" && resource.endpoint !== undefined) {
      // //화상회의 장비가 있는 회의실
      // setIsBookEndpoint(true);
    } else {
      Dialog.show({
        className: `${style.dialog} ${style.confirm}`,
        bodyClassName: `${style.dialog_body}`,
        bodyStyle: { padding: 0 },
        content: <BookResource onSubmit={() => onBook(resource)} />,
      });
    }
  };

  const onRefresh = () => {
    setResource(null);
    setIsSearch(true);
  };

  const contentRender = () => {
    switch (view) {
      case defaultType.type:
        return (
          <article className={style.ticket_suggest}>
            <div className={style.suggest_icon}>
              {t("HOTDESKING.MENU.NOT_USE.TITLE")}
            </div>
            <p className={style.suggest_title}>
              {t("HOTDESKING.MENU.NOT_USE.DESC.DESC_3", { NAME: user.name })}
            </p>
          </article>
        );
      case expandType.type:
        return (
          <section
            className={style.ticket_expand}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <section className={style.expand_content}>
              <section className={style.content_header}>
                <p>Today</p>
              </section>
              <section className={style.content_body}>
                <div
                  className={style.not_use_ticket_box}
                  style={{
                    maskImage:
                      "url('" +
                      process.env.REACT_APP_CLIENT_URL +
                      "img/hotdesking/suggest.png')",
                  }}
                >
                  <div className={style.not_use_top_box}>
                    <div className={style.banner}>
                      <p>{t("HOTDESKING.MENU.NOT_USE.TITLE")}</p>
                    </div>

                    <div className={style.comment}>
                      <p>
                        {t("HOTDESKING.MENU.NOT_USE.DESC.DESC_1", {
                          NAME: user.name,
                        })}
                      </p>
                      <p>{t("HOTDESKING.MENU.NOT_USE.DESC.DESC_2")}</p>
                    </div>
                  </div>

                  <div className={style.not_use_bottom_box}>
                    <p className={style.department}>
                      {resource?.location.departmentTitle}
                    </p>

                    <div className={style.location}>
                      <div className={style.item}>
                        <p>{resource?.location.title}</p>
                        <p>Location</p>
                      </div>

                      <span className={style.divider} />

                      <div className={style.item}>
                        <p>{resource?.title}</p>
                        <p>No.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className={style.content_footer}>
                <section className={style.footer_options}>
                  <div
                    className={style.item}
                    onClick={() =>
                      onShowResource(
                        resource?.department.id,
                        resource?.department.name,
                        resource?.location.id,
                        resource?.location.title,
                        resource?.location.source,
                        resource?.id,
                        resource?.title,
                        resource?.type,
                        resource?.coordinateX,
                        resource?.coordinateY
                      )
                    }
                  >
                    <SpotIcon />
                    <p>{t("HOTDESKING.MENU.NOT_USE.BUTTON.VIEW_SEAT")}</p>
                  </div>

                  <span className={style.divider} />

                  <div className={style.item} onClick={onRefresh}>
                    <ReloadIcon />
                    <p>
                      {t(
                        "HOTDESKING.MENU.NOT_USE.BUTTON.REQUEST_RECOMMENDATION"
                      )}
                    </p>
                  </div>
                </section>

                <section className={style.expand_footer}>
                  <Button
                    className={style.button}
                    onClick={() => onOpenBook()}
                    disabled={resource === null}
                  >
                    {t("HOTDESKING.MENU.NOT_USE.BUTTON.RESERVATION")}
                  </Button>
                </section>
              </section>
            </section>
          </section>
        );
      default:
        return <></>;
    }
  };
  return (
    <section
      className={`${style.ticket_container} ${style.notuse}`}
      style={{
        maxHeight: view === defaultType.type && defaultType.height - 64,
        height: "100%",
        width: "100%",
      }}
    >
      {isLoading || user === undefined ? (
        <Loading className={style.ticket_loading} />
      ) : (
        contentRender()
      )}
    </section>
  );
}
